/**
 * Cart Subscription Grouping — enhances the cart page with
 * three-way grouping (pack / subscription / one-time),
 * pack-level subscription toggle, per-line toggles for non-pack items,
 * cadence selector, and post-discount free shipping bar.
 */
(function () {
  'use strict';

  const FREE_SHIPPING = 60;
  const PACK_PROPERTY = '_glend_pack';
  const PACK_LABEL_PROPERTY = '_glend_pack_label';
  const PACK_SLOT_PROPERTY = '_glend_pack_slot';

  let sellingPlans = {};

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const container = document.querySelector('[data-cart-sub]');
    if (!container) return;

    try {
      sellingPlans = JSON.parse(container.dataset.sellingPlans || '{}');
    } catch (e) {
      sellingPlans = {};
    }

    initPackInstanceControls(container);
    initToggles(container);
    initCadence(container);
    initPromoCode(container);
    updateCartSummary(container);

    // Note: NOT subscribing to PUB_SUB_EVENTS.cartUpdate here. Cart.js's section
    // render (getSectionsToRender → replaces #main-cart-items > .js-contents) is
    // the single source of truth for summary values — the <aside class="cart-summary">
    // now lives inside .js-contents and is refreshed automatically with fresh
    // server-rendered HTML on every cart change. A JS-side updater would overwrite
    // those Liquid-rendered values with locally-formatted strings (mismatched
    // money format), causing a visible flicker.
  }

  // ---- Helpers: pack item identification + property preservation ----

  function isPackItem(item) {
    return !!(item.properties && item.properties[PACK_PROPERTY]);
  }

  function packInstanceIdOf(item) {
    return item && item.properties ? item.properties[PACK_PROPERTY] : null;
  }

  /**
   * Group cart items by their _glend_pack instance id.
   * Returns { [packInstanceId]: { items: [...], label: string } }.
   */
  function groupItemsByPackInstance(items) {
    const groups = {};
    items.forEach((item) => {
      if (!item.properties || !item.properties[PACK_PROPERTY]) return;
      const id = item.properties[PACK_PROPERTY];
      if (!groups[id]) {
        groups[id] = {
          items: [],
          label: item.properties[PACK_LABEL_PROPERTY] || 'Tu pack',
        };
      }
      groups[id].items.push(item);
    });
    return groups;
  }

  /**
   * Build a properties payload for a re-add, preserving all _glend_pack* keys
   * verbatim from the original item.
   */
  function preservePackProperties(item) {
    const props = {};
    if (!item.properties) return props;
    if (item.properties[PACK_PROPERTY]) props[PACK_PROPERTY] = item.properties[PACK_PROPERTY];
    if (item.properties[PACK_LABEL_PROPERTY]) props[PACK_LABEL_PROPERTY] = item.properties[PACK_LABEL_PROPERTY];
    if (item.properties[PACK_SLOT_PROPERTY]) props[PACK_SLOT_PROPERTY] = item.properties[PACK_SLOT_PROPERTY];
    return props;
  }

  // ---- Per-pack-instance subscribe toggle + cadence selector ----

  function initPackInstanceControls(container) {
    const controlsNodes = container.querySelectorAll('[data-pack-controls]');
    controlsNodes.forEach((node) => {
      const packEl = node.closest('[data-pack-id]');
      if (!packEl) return;
      const packInstanceId = packEl.dataset.packId;
      if (!packInstanceId) return;

      const toggle = node.querySelector('[data-pack-sub-toggle]');
      const cadence = node.querySelector('[data-pack-cadence]');

      if (toggle) {
        toggle.addEventListener('click', async () => {
          const isSubscribed = toggle.getAttribute('aria-pressed') === 'true';
          toggle.disabled = true;
          try {
            if (isSubscribed) {
              await unsubscribePackInstance(packInstanceId);
            } else {
              const planId = sellingPlans['4'] || Object.values(sellingPlans)[0];
              if (planId) {
                await subscribeAllPackItems(planId, packInstanceId);
              }
            }
            window.location.reload();
          } catch (err) {
            console.error('Pack toggle failed:', err);
            showToast('No hemos podido actualizar. Int\u00e9ntalo otra vez.', 'error');
          } finally {
            toggle.disabled = false;
          }
        });
      }

      if (cadence) {
        // Defensive sync (Bug C): correct the <select> against the live cart
        // selling plans, guarding against any stale section render.
        syncPackCadenceSelect(cadence, packInstanceId);

        cadence.addEventListener('change', async () => {
          const weeks = cadence.value;
          const planId = sellingPlans[weeks];
          if (!planId) return;
          try {
            await changeCadenceForPackInstance(planId, packInstanceId);
            window.location.reload();
          } catch (err) {
            console.error('Pack cadence update failed:', err);
            showToast('No hemos podido actualizar la cadencia. Int\u00e9ntalo otra vez.', 'error');
          }
        });
      }
    });
  }

  /**
   * Defensive cadence sync: read the live selling plan of a pack instance's
   * subscribed items and set the cadence <select>'s value to match. If the
   * instance's items carry differing plan ids (drift), leave the select as
   * rendered (server already flags the mixed state). Minimal, fire-and-forget.
   */
  async function syncPackCadenceSelect(selectEl, packInstanceId) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const subscribedPackItems = cart.items.filter(
        item => isPackItem(item)
          && packInstanceIdOf(item) === packInstanceId
          && item.selling_plan_allocation
      );
      if (!subscribedPackItems.length) return;

      const planIds = subscribedPackItems.map(
        item => String(item.selling_plan_allocation.selling_plan.id)
      );
      const uniquePlanIds = Array.from(new Set(planIds));
      if (uniquePlanIds.length > 1) return; // mixed cadence \u2014 leave as-is

      const livePlanId = uniquePlanIds[0];
      const weeks = Object.keys(sellingPlans).find(
        w => String(sellingPlans[w]) === livePlanId
      );
      if (weeks && selectEl.value !== weeks) {
        selectEl.value = weeks;
      }
    } catch (err) {
      console.error('Pack cadence sync failed:', err);
    }
  }

  /**
   * Replace a set of pack line items with new versions (optionally with a
   * different selling_plan).
   *
   * Two requests total regardless of pack size:
   *   1. /cart/update.js with a positional `updates` array — zeros every
   *      targeted line in one shot, leaves untouched lines at their current
   *      quantity. Positional form (vs variant_id keys) is mandatory: same
   *      variant can appear in two packs or in non-pack lines, and we only
   *      want to zero the lines we picked.
   *   2. /cart/add.js with `items: [...]` — single batched add for every
   *      replacement.
   *
   * Caller passes the cart it just fetched so we don't re-hit /cart.js here.
   */
  async function replacePackLines(itemsToReplace, sellingPlanId, cart) {
    if (!itemsToReplace.length) return;

    const targetKeys = new Set(
      itemsToReplace.map(it => it.key).filter(Boolean)
    );
    const updates = cart.items.map(
      it => (targetKeys.has(it.key) ? 0 : it.quantity)
    );
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    const itemsPayload = itemsToReplace.map((item) => {
      const payload = {
        id: item.variant_id,
        quantity: item.quantity,
        properties: preservePackProperties(item)
      };
      if (sellingPlanId) payload.selling_plan = parseInt(sellingPlanId);
      return payload;
    });
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: itemsPayload })
    });
  }

  async function subscribeAllPackItems(sellingPlanId, packInstanceId) {
    const cart = await fetch('/cart.js').then(r => r.json());
    const packItems = cart.items.filter(
      item => isPackItem(item)
        && packInstanceIdOf(item) === packInstanceId
        && !item.selling_plan_allocation
    );
    await replacePackLines(packItems, sellingPlanId, cart);
  }

  async function unsubscribePackInstance(packInstanceId) {
    const cart = await fetch('/cart.js').then(r => r.json());
    const packItems = cart.items.filter(
      item => isPackItem(item)
        && packInstanceIdOf(item) === packInstanceId
        && item.selling_plan_allocation
    );
    await replacePackLines(packItems, null, cart);
  }

  async function changeCadenceForPackInstance(sellingPlanId, packInstanceId) {
    const cart = await fetch('/cart.js').then(r => r.json());
    const subscribedPackItems = cart.items.filter(
      item => isPackItem(item)
        && packInstanceIdOf(item) === packInstanceId
        && item.selling_plan_allocation
    );
    await replacePackLines(subscribedPackItems, sellingPlanId, cart);
  }

  // ---- Per-line subscribe toggles (non-pack items only) ----
  function initToggles(container) {
    container.querySelectorAll('[data-cart-sub-toggle]').forEach(toggle => {
      toggle.addEventListener('click', async () => {
        const lineKey = toggle.dataset.lineKey;
        const variantId = toggle.dataset.variantId;
        const quantity = parseInt(toggle.dataset.quantity || '1');
        const isSubscribed = toggle.getAttribute('aria-checked') === 'true';

        toggle.disabled = true;

        try {
          if (isSubscribed) {
            await toggleLineSellingPlan(lineKey, variantId, quantity, null);
          } else {
            const planId = sellingPlans['4'] || Object.values(sellingPlans)[0];
            if (planId) {
              await toggleLineSellingPlan(lineKey, variantId, quantity, planId);
            }
          }
          window.location.reload();
        } catch (err) {
          console.error('Toggle failed:', err);
          showToast('No hemos podido actualizar. Int\u00e9ntalo otra vez.', 'error');
        } finally {
          toggle.disabled = false;
        }
      });
    });
  }

  async function toggleLineSellingPlan(lineKey, variantId, quantity, sellingPlanId) {
    // Remove the existing line
    const removeRes = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!removeRes.ok) throw new Error('Cart remove failed');

    // Re-add with or without selling plan (no pack property for non-pack items)
    const addBody = { items: [{ id: parseInt(variantId), quantity: quantity }] };
    if (sellingPlanId) {
      addBody.items[0].selling_plan = parseInt(sellingPlanId);
    }

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addBody)
    });
    if (!addRes.ok) throw new Error('Cart re-add failed');
    return addRes.json();
  }

  // ---- Cadence selector (non-pack subscriptions) ----
  function initCadence(container) {
    container.querySelectorAll('[data-cadence-btn]').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('[data-cadence-btn]').forEach(b => {
          b.setAttribute('aria-checked', 'false');
        });
        btn.setAttribute('aria-checked', 'true');

        const weeks = btn.dataset.cadenceBtn;
        const planId = sellingPlans[weeks];
        if (planId) {
          updateAllSubscribedLines(container, planId);
        }
      });
    });
  }

  async function updateAllSubscribedLines(container, planId) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const nonPackSubItems = cart.items.filter(
        item => item.selling_plan_allocation && !isPackItem(item)
      );

      if (nonPackSubItems.length) {
        const targetKeys = new Set(nonPackSubItems.map(it => it.key));
        const updates = cart.items.map(
          it => (targetKeys.has(it.key) ? 0 : it.quantity)
        );
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: nonPackSubItems.map((item) => ({
              id: item.variant_id,
              quantity: item.quantity,
              selling_plan: parseInt(planId)
            }))
          })
        });
      }

      window.location.reload();
    } catch (err) {
      console.error('Cadence update failed:', err);
      showToast('No hemos podido actualizar la cadencia. Int\u00e9ntalo otra vez.', 'error');
    }
  }

  function initPromoCode(container) {
    const toggle = container.querySelector('[data-promo-toggle]');
    const form = container.querySelector('[data-promo-form]');
    if (toggle && form) {
      toggle.addEventListener('click', () => {
        form.classList.toggle('is-open');
        const input = form.querySelector('.cart-sub__promo-input');
        if (form.classList.contains('is-open') && input) input.focus();
      });
    }

    const submitBtn = container.querySelector('.cart-sub__promo-submit');
    const input = container.querySelector('.cart-sub__promo-input');
    if (submitBtn && input) {
      const applyCode = () => {
        const code = input.value.trim();
        if (code) {
          window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
        }
      };
      submitBtn.addEventListener('click', applyCode);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); applyCode(); }
      });
    }
  }

  async function updateCartSummary(container) {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      const subtotalCents = cart.total_price;
      const subtotalEur = subtotalCents / 100;

      let packTotal = 0;
      let subTotal = 0;
      let onetimeTotal = 0;
      let allSavings = 0;

      cart.items.forEach(item => {
        const isPack = isPackItem(item);
        if (isPack) {
          packTotal += item.final_line_price;
          allSavings += (item.original_line_price - item.final_line_price);
        } else if (item.selling_plan_allocation) {
          subTotal += item.final_line_price;
          allSavings += (item.original_line_price - item.final_line_price);
        } else {
          onetimeTotal += item.final_line_price;
        }
      });

      const packEl = container.querySelector('[data-summary-pack]');
      const subEl = container.querySelector('[data-summary-sub]');
      const onetimeEl = container.querySelector('[data-summary-onetime]');
      const savingsEl = container.querySelector('[data-summary-savings]');
      const totalEl = container.querySelector('[data-summary-total]');
      const savingsNote = container.querySelector('[data-savings-note]');

      if (packEl) packEl.textContent = formatMoney(packTotal);
      if (subEl) subEl.textContent = formatMoney(subTotal);
      if (onetimeEl) onetimeEl.textContent = formatMoney(onetimeTotal);
      if (savingsEl) {
        savingsEl.textContent = '-' + formatMoney(allSavings);
        savingsEl.closest('.cart-sub__summary-row').style.display = allSavings > 0 ? '' : 'none';
      }
      // Total = items subtotal + estimated non-pack shipping. Mirrors the
      // Liquid in main-cart-items.liquid: pack ships free, non-pack pays €4.95
      // (Peninsula default) when non-pack subtotal < €60, else free.
      const nonPackCents = subTotal + onetimeTotal;
      const hasNonPack = nonPackCents > 0;
      const estShipCents = hasNonPack && nonPackCents < 6000 ? 495 : 0;
      if (totalEl) totalEl.textContent = formatMoney(subtotalCents + estShipCents);
      if (savingsNote) {
        if (allSavings > 0) {
          savingsNote.textContent = `Ahorras ${formatMoney(allSavings)} con suscripci\u00f3n`;
          savingsNote.style.display = '';
        } else {
          savingsNote.style.display = 'none';
        }
      }

      // Shipping bar
      const shippingFill = container.querySelector('[data-cart-shipping-fill]');
      const shippingText = container.querySelector('[data-cart-shipping-text]');
      if (shippingFill) {
        const pct = Math.min(100, (subtotalEur / FREE_SHIPPING) * 100);
        shippingFill.style.width = pct + '%';
        if (subtotalEur >= FREE_SHIPPING) {
          shippingFill.style.backgroundColor = '#2E8B57';
          if (shippingText) shippingText.textContent = 'Env\u00edo gratis incluido';
        } else {
          shippingFill.style.backgroundColor = '#F47C35';
          const remaining = (FREE_SHIPPING - subtotalEur).toFixed(2).replace('.', ',');
          if (shippingText) shippingText.textContent = `Te faltan ${remaining} \u20AC para env\u00edo gratis`;
        }
      }

      // Checkout button
      const checkoutBtn = container.querySelector('[data-cart-checkout]');
      if (checkoutBtn) {
        checkoutBtn.disabled = cart.item_count === 0;
      }
    } catch (err) {
      console.error('Cart summary update failed:', err);
    }
  }

  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
  }

  function showToast(message, type) {
    const toast = document.createElement('div');
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.style.cssText = `
      position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
      background: ${type === 'error' ? '#B3261E' : '#2C2A2A'}; color: #fff;
      padding: 12px 24px; border-radius: 999px; font-size: 14px; font-weight: 600;
      z-index: 9999; box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }
})();
